import { useState } from 'react'
import { Link } from 'react-router-dom'
import {
  BadgeCheck, ChevronDown, ChevronRight, FileText, Pin, PinOff, ScrollText, Swords,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { fmtDateTime } from '@/shared/lib/format'
import { Note } from '@/shared/ui/states'
import type { Finding, Proposal, Report } from '../api'
import { FINDING_KIND_LABEL, SEVERITY_CLASS, SEVERITY_ORDER, usd } from '../vocab'
import { ReportVisual } from './ReportVisual'
import { ProposalCard } from './ProposalCard'

/**
 * A finished report: what it concluded, what it drew, and what it wants done.
 *
 * ## Confidence is a word, never a colour
 *
 * Severity is a property of the world and gets the app's status colours like any
 * other severity. Confidence is the machine's assessment of its own claim, and
 * painting it green would lend it exactly the authority this module withholds.
 * It is rendered as a word plus the rationale — including the sentence the server
 * appends when the platform CAPPED it because the red team could not run.
 *
 * ## Contested findings are marked, not hidden
 *
 * When the red team refused to accept a finding the analyst kept, the disagreement
 * is shown beside the finding. Two models from two companies disagreeing is more
 * informative than either alone; dressing it as an error would teach people to
 * skip exactly the findings worth reading twice.
 */

export function ReportView({
  report,
  onPin,
  onProposalChanged,
  compact,
}: {
  report: Report
  onPin?: (pinned: boolean) => void
  onProposalChanged?: (proposal: Proposal) => void
  compact?: boolean
}) {
  const bound = report.visuals.filter((v) => v.bindingStatus === 'BOUND')
  const dropped = report.visuals.filter((v) => v.bindingStatus === 'UNBOUND_DROPPED')
  const findings = [...report.findings].sort(
    (a, b) => SEVERITY_ORDER.indexOf(a.severity) - SEVERITY_ORDER.indexOf(b.severity))

  return (
    <div className="space-y-4">
      <header className="space-y-1.5">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0">
            <h2 className="text-erp-lg font-semibold">{report.title}</h2>
            <p className="text-erp-sm text-muted-foreground mt-0.5">{report.headline}</p>
          </div>
          {onPin && (
            <button type="button" onClick={() => onPin(!report.pinned)}
              className="erp-btn h-8 px-2.5 text-erp-xs inline-flex items-center gap-1.5">
              {report.pinned ? <PinOff className="h-3.5 w-3.5" /> : <Pin className="h-3.5 w-3.5" />}
              {report.pinned ? 'Unpin' : 'Pin to the console'}
            </button>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-erp-xs text-muted-foreground">
          <span className="inline-flex items-center gap-1">
            <BadgeCheck className="h-3 w-3" />
            {report.confidence} confidence
          </span>
          {report.generatedByModel && <span>analysed by {report.generatedByModel}</span>}
          {report.verifiedByModel
            ? <span className="inline-flex items-center gap-1">
                <Swords className="h-3 w-3" />red-teamed by {report.verifiedByModel}
              </span>
            : <span className="text-amber-700 dark:text-amber-400">not red-teamed</span>}
          <span>{fmtDateTime(report.createdAt)}</span>
          {report.run && <span className="tabular-nums">{usd(report.run.costUsd)}</span>}
        </div>

        <p className="text-erp-xs text-muted-foreground/80">{report.confidenceRationale}</p>
      </header>

      <section className="rounded-md border border-border bg-muted/30 px-3 py-2.5">
        <div className="text-erp-xs font-medium uppercase tracking-wide text-muted-foreground mb-1">
          Executive summary
        </div>
        <p className="text-erp-sm whitespace-pre-wrap">{report.executiveSummary}</p>
      </section>

      {report.disagreement && report.disagreement.trim() && (
        <Note tone="info">
          <span className="font-medium">Where the council disagreed. </span>
          {report.disagreement}
        </Note>
      )}

      {bound.length > 0 && (
        <section className={cn('grid gap-4', compact ? '' : 'md:grid-cols-2')}>
          {bound.map((visual) => (
            <ReportVisual key={visual.id} visual={visual}
              className={visual.kind === 'CHOROPLETH' ? 'md:col-span-2' : undefined} />
          ))}
        </section>
      )}

      {findings.length > 0 && (
        <section>
          <h3 className="text-erp-sm font-medium mb-1.5">
            Findings <span className="text-muted-foreground">({findings.length})</span>
          </h3>
          <ul className="space-y-2">
            {findings.map((finding) => (
              <FindingCard key={finding.id} finding={finding} />
            ))}
          </ul>
        </section>
      )}

      {report.proposals.length > 0 && (
        <section>
          <h3 className="text-erp-sm font-medium mb-1.5">
            Proposals <span className="text-muted-foreground">({report.proposals.length})</span>
          </h3>
          <p className="text-erp-xs text-muted-foreground mb-2">
            Nothing here has changed anything. A proposal is approved by one person and applied
            by another, under that change&rsquo;s own authority.
          </p>
          <ul className="space-y-2">
            {report.proposals.map((proposal) => (
              <ProposalCard key={proposal.id} proposal={proposal}
                onChanged={onProposalChanged} />
            ))}
          </ul>
        </section>
      )}

      {report.bodyMarkdown && <Body markdown={report.bodyMarkdown} />}

      {dropped.length > 0 && (
        <section className="space-y-2">
          <h3 className="text-erp-xs font-medium uppercase tracking-wide text-muted-foreground">
            Charts that did not resolve ({dropped.length})
          </h3>
          <p className="text-erp-xs text-muted-foreground">
            The editor asked for these and the data it named was not in the brief. They are kept
            rather than hidden: a rising count here is the earliest sign that a prompt has
            drifted.
          </p>
          {dropped.map((visual) => <ReportVisual key={visual.id} visual={visual} />)}
        </section>
      )}

      {report.run && (
        <footer className="border-t border-border pt-2 text-erp-xs text-muted-foreground">
          <Link to={`/advisory/runs/${report.run.id}`} className="inline-flex items-center gap-1
            hover:text-foreground">
            <ScrollText className="h-3 w-3" />
            See the council, the models and the evidence brief behind this report
          </Link>
        </footer>
      )}
    </div>
  )
}

function FindingCard({ finding }: { finding: Finding }) {
  const [open, setOpen] = useState(false)
  return (
    <li className="rounded-md border border-border">
      <button type="button" onClick={() => setOpen((v) => !v)}
        className="w-full text-left px-3 py-2 flex items-start gap-2">
        {open ? <ChevronDown className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              : <ChevronRight className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className={cn('rounded border px-1.5 py-px text-erp-xs',
              SEVERITY_CLASS[finding.severity])}>
              {finding.severity}
            </span>
            <span className="text-erp-xs text-muted-foreground">
              {FINDING_KIND_LABEL[finding.kind] ?? finding.kind}
            </span>
            {finding.contested && (
              <span className="rounded border border-amber-200 bg-amber-50 px-1.5 py-px
                text-erp-xs text-amber-700 dark:bg-amber-500/10 dark:text-amber-400">
                disputed by the red team
              </span>
            )}
          </div>
          <div className="text-erp-sm font-medium mt-1">{finding.title}</div>
          {/* The consequence, always visible. It is the reason the finding exists. */}
          <div className="text-erp-sm text-muted-foreground mt-0.5">{finding.soWhat}</div>
        </div>
      </button>

      {open && (
        <div className="border-t border-border px-3 py-2 space-y-2">
          <p className="text-erp-sm whitespace-pre-wrap">{finding.detail}</p>

          {finding.contested && finding.contestedNote && (
            <div className="rounded-md border border-amber-200 bg-amber-50/60 px-2.5 py-2
              dark:bg-amber-500/5">
              <div className="text-erp-xs font-medium text-amber-800 dark:text-amber-300">
                The red team&rsquo;s objection
              </div>
              <p className="text-erp-xs text-amber-900/90 dark:text-amber-200/90 mt-0.5">
                {finding.contestedNote}
              </p>
            </div>
          )}

          {finding.evidence.length > 0 && (
            <div className="text-erp-xs text-muted-foreground">
              <span className="font-medium">Evidence: </span>
              {finding.evidence.map((e, i) => (
                <span key={i} className="mr-2">
                  <code className="rounded bg-muted px-1 py-px">{e.datasetId}</code>
                  {e.rows && e.rows.length > 0 && ` rows ${e.rows.join(', ')}`}
                  {e.note ? ` — ${e.note}` : ''}
                </span>
              ))}
            </div>
          )}

          {finding.geoUnitIds.length > 0 && (
            <div className="text-erp-xs text-muted-foreground">
              Concerns {finding.geoUnitIds.length} unit
              {finding.geoUnitIds.length === 1 ? '' : 's'}
              {finding.geoMetric != null && finding.geoMetricLabel
                && ` · ${finding.geoMetricLabel}: ${finding.geoMetric}`}
            </div>
          )}

          {finding.confidence != null && (
            <div className="text-erp-xs text-muted-foreground/70">
              The analyst put its own confidence at {(finding.confidence * 100).toFixed(0)}%.
            </div>
          )}
        </div>
      )}
    </li>
  )
}

/**
 * The long form.
 *
 * Rendered as pre-wrapped text with headings picked out, not as parsed markdown.
 * There is no markdown renderer in this app, and adding one to display model
 * output — the least trusted text in the system — is not the place to start.
 */
function Body({ markdown }: { markdown: string }) {
  const [open, setOpen] = useState(false)
  return (
    <section>
      <button type="button" onClick={() => setOpen((v) => !v)}
        className="erp-btn h-8 px-2.5 text-erp-xs inline-flex items-center gap-1.5">
        <FileText className="h-3.5 w-3.5" />
        {open ? 'Hide the full analysis' : 'Read the full analysis'}
      </button>
      {open && (
        <div className="mt-2 rounded-md border border-border px-3 py-2.5 space-y-1">
          {markdown.split('\n').map((line, i) => {
            const heading = /^#{1,4}\s+(.*)$/.exec(line)
            if (heading) {
              return (
                <div key={i} className="text-erp-sm font-medium pt-2 first:pt-0">
                  {heading[1]}
                </div>
              )
            }
            if (!line.trim()) return <div key={i} className="h-1" />
            return (
              <p key={i} className="text-erp-sm text-muted-foreground whitespace-pre-wrap">
                {line}
              </p>
            )
          })}
        </div>
      )}
    </section>
  )
}
